import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BotonAcceso from '../elementos/BotonAcceso';
import { useTheme } from "../guardarToken/themeContext";
import { UserPlus, Save, ArrowLeft, Shield, Eye, EyeOff } from 'lucide-react';

const apiUrlUsers = process.env.REACT_APP_API_USERS || '/api/users/users/';
const apiUrlSectores = process.env.REACT_APP_API_SECTORES || '/api/sectores/section/';
const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES || '/api/direcciones/addres/';
const apiUrlSedes = process.env.REACT_APP_API_SEDES || '/api/sedes/';

const CreateUser = () => {
    const [usuario, setUsuario] = useState('');
    const [pass, setPass] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [nombreuser, setNombreUser] = useState('');
    const [apellido, setApellido] = useState('');
    const [tipo, setTipo] = useState('user');

    // Jerarquía de ubicación
    const [sedeId, setSedeId] = useState('');
    const [direccionId, setDireccionId] = useState('');
    const [sectorId, setSectorId] = useState('');

    const [sedes, setSedes] = useState([]);
    const [direcciones, setDirecciones] = useState([]);
    const [sectores, setSectores] = useState([]);

    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [sedesRes, diresRes, sectoresRes] = await Promise.all([
                    axios.get(apiUrlSedes),
                    axios.get(apiUrlDirecciones),
                    axios.get(apiUrlSectores)
                ]);
                setSedes(sedesRes.data);
                setDirecciones(diresRes.data);
                setSectores(sectoresRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchInitialData();
    }, []);

    // Filtrado dinámico
    const direccionesFiltradas = direcciones.filter(d => !sedeId || d.sede_id == sedeId);
    const sectoresFiltrados = sectores.filter(s => !direccionId || s.id_direccion == direccionId);

    const store = async (e) => {
        e.preventDefault();
        try {
            // Buscamos los nombres para compatibilidad con campos string si aún se usan en el back para algo
            const sedeObj = sedes.find(s => s.id === parseInt(sedeId));
            const direObj = direcciones.find(d => d.id_direccion === parseInt(direccionId));
            const sectorObj = sectores.find(s => s.id_sector === parseInt(sectorId));

            await axios.post(apiUrlUsers, {
                usuario,
                pass,
                nombreuser,
                apellido,
                sede_id: sedeId || null,
                ubicacion: direObj ? direObj.direccion : '', // fallback string
                tipo,
                sector: sectorObj ? sectorObj.sector : '', // fallback string
                id_sector: sectorId || null,
                sede: sedeObj ? sedeObj.nombre : '' // fallback string
            });
            navigate('/admin/AbmUsuarios');
        } catch (error) {
            console.error('Error al crear el usuario:', error);
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 lg:p-8 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
            <div className={`w-full max-w-4xl ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-100"} rounded-[2.5rem] shadow-2xl border p-8 lg:p-12 animate-in zoom-in duration-500`}>

                <div className="flex flex-col items-center mb-10">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-5 rounded-3xl shadow-xl shadow-indigo-900/30 mb-5">
                        <UserPlus className="text-white w-10 h-10" />
                    </div>
                    <h2 className="text-3xl font-black tracking-tighter mb-2">Crear Usuario</h2>
                    <p className={`text-[11px] font-black uppercase tracking-[0.2em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Configuración de perfil y accesos</p>
                </div>

                <form className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" onSubmit={store}>
                    {/* DATOS PERSONALES */}
                    <div className="space-y-6">
                        <h3 className="text-xs font-black uppercase tracking-widest text-indigo-500 border-b border-indigo-500/20 pb-2 mb-4">Información Personal</h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Nombre</label>
                                <input type="text" placeholder="Ej: Juan" value={nombreuser} onChange={(e) => setNombreUser(e.target.value)}
                                    className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                            </div>
                            <div>
                                <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Apellido</label>
                                <input type="text" placeholder="Ej: Pérez" value={apellido} onChange={(e) => setApellido(e.target.value)}
                                    className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Usuario</label>
                            <input type="text" placeholder="juan.perez" value={usuario} onChange={(e) => setUsuario(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Contraseña</label>
                            <div className="relative">
                                <input type={showPassword ? "text" : "password"} placeholder="••••••••" value={pass} onChange={(e) => setPass(e.target.value)}
                                    className={`w-full px-5 pr-14 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDarkMode ? "text-slate-500 hover:text-indigo-400" : "text-slate-400 hover:text-indigo-600"}`}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Rol de Usuario</label>
                            <div className="relative">
                                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500" />
                                <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                                    className={`w-full pl-12 pr-5 py-3.5 rounded-2xl text-sm font-bold appearance-none transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`}>
                                    <option value="user">Colaborador (User)</option>
                                    <option value="admin">Administrador (Admin)</option>
                                    <option value="invitado">Invitado</option>
                                    <option value="direccion">Director / Gerente</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* UBICACIÓN Y ESTRUCTURA */}
                    <div className="space-y-6">
                        <h3 className="text-xs font-black uppercase tracking-widest text-emerald-500 border-b border-emerald-500/20 pb-2 mb-4">Ubicación y Sector</h3>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Sede</label>
                            <select value={sedeId} onChange={(e) => { setSedeId(e.target.value); setDireccionId(''); setSectorId(''); }}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required>
                                <option value="">Seleccione Sede</option>
                                {sedes.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Dirección</label>
                            <select value={direccionId} onChange={(e) => { setDireccionId(e.target.value); setSectorId(''); }}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required>
                                <option value="">Seleccione Dirección</option>
                                {direccionesFiltradas.map(d => <option key={d.id_direccion} value={d.id_direccion}>{d.direccion}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Sector</label>
                            <select value={sectorId} onChange={(e) => setSectorId(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required>
                                <option value="">Seleccione Sector</option>
                                {sectoresFiltrados.map(s => <option key={s.id_sector} value={s.id_sector}>{s.sector}</option>)}
                            </select>
                        </div>

                        <div className="pt-4 flex gap-4">
                            <BotonAcceso variant="accent" type='submit'
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-widest h-16 rounded-2xl shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-2 transform active:scale-95 transition-all">
                                <Save className="w-5 h-5" /> Crear Usuario
                            </BotonAcceso>
                            <BotonAcceso variant="dark" type='button' onClick={() => navigate('/admin/AbmUsuarios')}
                                className={`px-8 ${isDarkMode ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600"} font-black text-xs uppercase tracking-widest h-16 rounded-2xl flex items-center justify-center gap-2 transition-all`}>
                                <ArrowLeft className="w-5 h-5" /> Volver
                            </BotonAcceso>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateUser;
